import { Link } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import {
  isTerminalTaskStatus,
  parseTaskActionFormSchema,
  type TaskTypeAction,
} from '@erp/shared-types';
import type { TaskResponse, TaskTypeResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { toast } from 'src/components/ui/toast';
import { useExecuteTaskAction } from 'src/api/rpc/hooks/tasks/useTaskDetail';
import type { TaskDetailSearch } from './taskDetailNav';

function queueActionRecordedMessage(
  gt: ReturnType<typeof useGT>,
  kind: 'emit_event' | 'start_workflow' | 'set_outcome',
): string {
  if (kind === 'set_outcome') {
    return gt('Action recorded — outcome saved', {
      context: 'toast after a queue quick action recorded an outcome',
    });
  }
  if (kind === 'start_workflow') {
    return gt('Action recorded — workflow started', {
      context: 'toast after a queue quick action started a workflow',
    });
  }
  return gt('Action recorded', {
    context: 'toast after a queue quick action was executed',
  });
}

/**
 * True when the action should open task detail instead of running inline:
 * it has a form, or its formSchema is invalid (detail surfaces the config error).
 */
function actionNeedsDetail(action: TaskTypeAction): boolean {
  const parsed = parseTaskActionFormSchema(action.formSchema);
  return !parsed.ok || (parsed.fields !== null && parsed.fields.length > 0);
}

/**
 * Queue-row quick actions for a task type's declared actions.
 * - Formless actions run immediately via `tasks.executeAction`.
 * - Actions with a formSchema (or an invalid schema) link to task detail.
 * Secondary actions (snooze, open in new tab) live in the row's `⋮` menu.
 */
export function TaskQueueQuickActions({
  task,
  taskType,
  detailSearch,
}: {
  task: TaskResponse;
  taskType: TaskTypeResponse | undefined;
  detailSearch?: TaskDetailSearch;
}) {
  const gt = useGT();
  const executeAction = useExecuteTaskAction(task.id);
  const actions = taskType?.actions ?? [];
  if (actions.length === 0 || isTerminalTaskStatus(task.status)) {
    return null;
  }

  async function runFormless(action: TaskTypeAction) {
    try {
      const result = await executeAction.mutateAsync({
        id: task.id,
        actionKey: action.key,
      });
      toast.success(queueActionRecordedMessage(gt, result.effect.kind));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Action failed', {
              context: 'toast after a queue quick action failed',
            }),
      );
    }
  }

  return (
    // Stretch-link rows need a stacking context + click isolation so buttons
    // don't navigate to the task detail page.
    <div
      className="relative z-10 flex flex-wrap items-center gap-1"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      // Keep the browser's own menu on quick-action links instead of the row menu.
      onContextMenu={(event) => event.stopPropagation()}
    >
      {actions.map((action) => {
        if (actionNeedsDetail(action)) {
          return (
            <Button key={action.key} variant="outline" size="sm" asChild>
              <Link
                to="/tasks/$taskId"
                params={{ taskId: task.id }}
                search={detailSearch}
                onClick={(event) => event.stopPropagation()}
              >
                {action.label}
              </Link>
            </Button>
          );
        }

        return (
          <Button
            key={action.key}
            variant="outline"
            size="sm"
            disabled={executeAction.isPending}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void runFormless(action);
            }}
          >
            {action.label}
          </Button>
        );
      })}
    </div>
  );
}
